import { Link } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import PublicHeader from "@/components/PublicHeader";
import PublicFooter from "@/components/PublicFooter";
import ChatWidget from "@/components/ChatWidget";
import Seo from "@/components/Seo";
import Editable from "@/components/Editable";
import api from "@/lib/api";
import { ArrowRight, PlayCircle, Clock, House, MagnifyingGlass, Funnel } from "@phosphor-icons/react";
import { SERVICES } from "@/data/services";
import { stripHtml } from "@/lib/stripHtml";

const ALL = "all";

function pickServiceSlug(title) {
  const n = (title || "").toLowerCase();
  for (const s of SERVICES) {
    const t = (s.title || "").toLowerCase();
    if (t && n.includes(t)) return s.slug;
  }
  return null;
}

export default function CoursesOnline() {
  const [courses, setCourses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState(ALL);

  useEffect(() => {
    api.get("/courses?course_type=online").then((r) => setCourses(r.data)).catch(() => {});
    api.get("/course-categories").then((r) => setCategories(r.data)).catch(() => {});
    // Track interest — logged-in clients browsing the online catalogue land
    // an entry on their admin profile.
    api.post("/activity/track-interest", { kind: "course", ref_id: "online-catalogue", label: "Online courses catalogue" }).catch(() => {});
  }, []);

  const filtered = useMemo(() => courses.filter((c) => {
    if (category !== ALL && (c.category || "") !== category) return false;
    if (q.trim()) {
      const t = q.trim().toLowerCase();
      const hay = `${c.title || ""} ${stripHtml(c.description) || ""} ${c.category || ""} ${c.level || ""}`.toLowerCase();
      if (!hay.includes(t)) return false;
    }
    return true;
  }), [courses, q, category]);

  // Build categories — use server categories, fall back to those in use
  const categoryOptions = categories.length
    ? categories.map((c) => c.name)
    : Array.from(new Set(courses.map((c) => c.category).filter(Boolean)));

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <Seo title="Online Puppy Training Courses | Puppl" description="Self-paced online puppy training courses from the Puppl team — train at your own pace, in your own space. Foundations, recall, tricks and more." canonical={typeof window !== "undefined" ? window.location.origin + "/courses/online" : ""} />
      <PublicHeader />

      <section className="bg-[#1b4a76] text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20 lg:py-24">
          <Editable id="courses_online.eyebrow" as="p" className="text-sm uppercase tracking-wider font-semibold text-[#E2B054] mb-3" defaultHtml="Online courses" />
          <Editable id="courses_online.heading" as="h1" className="font-heading text-4xl lg:text-6xl font-bold leading-tight mb-4 max-w-3xl" defaultHtml="Train at your own pace, in your own space." />
          <Editable id="courses_online.subtitle" as="p" className="text-lg lg:text-xl opacity-90 max-w-2xl" defaultHtml="Self-paced video courses from puppy specialist the Puppl team — from basic training and help to fun tricks and activities. Watch, pause, repeat." />
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/register" className="btn-primary inline-flex items-center gap-2">Get Started <ArrowRight size={18} weight="bold" /></Link>
            <Link to="/courses/in-person" className="bg-white/15 backdrop-blur-sm hover:bg-white/25 text-white border border-white/30 rounded-xl px-6 py-3 font-semibold transition-colors inline-flex items-center gap-2">
              <House size={18} weight="duotone" /> Prefer in-person?
            </Link>
          </div>
        </div>
      </section>

      <section className="py-16 px-6 lg:px-12 max-w-7xl mx-auto">
        {/* Filter bar */}
        <div className="card-pp p-4 sm:p-5 mb-6" data-testid="online-course-filters">
          <div className="flex items-center gap-2 mb-3 text-stone-700">
            <Funnel size={16} weight="duotone" className="text-[#f38838]" />
            <span className="text-sm font-semibold">Find a course</span>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="relative">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
              <input data-testid="course-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search course title…" className="w-full rounded-lg border border-stone-200 pl-9 pr-3 py-2.5 text-sm" />
            </div>
            <select data-testid="course-category-filter" value={category} onChange={(e) => setCategory(e.target.value)} className="w-full rounded-lg border border-stone-200 px-3 py-2.5 text-sm bg-white">
              <option value={ALL}>All categories</option>
              {categoryOptions.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          {(category !== ALL || q) && (
            <div className="mt-3 flex items-center justify-between text-xs text-stone-500">
              <span>{filtered.length} course{filtered.length !== 1 && "s"} match</span>
              <button onClick={() => { setCategory(ALL); setQ(""); }} className="text-[#f38838] font-semibold hover:underline">Clear filters</button>
            </div>
          )}
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((c) => (
            <article key={c.id} className="card-pp overflow-hidden flex flex-col" data-testid={`online-course-${c.id}`}>
              <div className="aspect-[16/10] overflow-hidden relative">
                {c.cover_image ? <img src={c.cover_image} alt={c.title} className="w-full h-full object-cover" loading="lazy" /> : <div className="w-full h-full bg-stone-200 flex items-center justify-center"><PlayCircle size={32} className="text-stone-400" weight="duotone" /></div>}
                <span className="absolute top-3 left-3 bg-white/90 backdrop-blur-sm text-xs font-bold px-2.5 py-1 rounded-full text-stone-800">{c.level}</span>
                {c.category && <span className="absolute top-3 right-3 bg-[#f38838] text-white text-xs font-bold px-2.5 py-1 rounded-full">{c.category}</span>}
              </div>
              <div className="p-6 flex-1 flex flex-col">
                <div className="flex items-center gap-3 text-xs text-stone-500 mb-2">
                  <PlayCircle size={14} weight="fill" className="text-[#f38838]" />
                  <span>{c.lesson_count} lessons</span>
                </div>
                <h3 className="font-heading font-semibold text-xl text-stone-900 mb-2">{c.title}</h3>
                <p className="text-sm text-stone-600 line-clamp-3 mb-4 flex-1">{stripHtml(c.description)}</p>
                <div className="flex items-center justify-between mt-auto">
                  <div className="font-heading text-2xl font-bold text-[#f38838]">${c.price}</div>
                  <Link to={pickServiceSlug(c.title) ? `/services/${pickServiceSlug(c.title)}` : "/services"} className="btn-primary text-sm inline-flex items-center gap-1.5" data-testid={`online-course-info-${c.id}`}>
                    More info <ArrowRight size={14} weight="bold" />
                  </Link>
                </div>
              </div>
            </article>
          ))}
          {filtered.length === 0 && (
            <div className="col-span-full text-center py-16 text-stone-500">
              <PlayCircle size={48} weight="duotone" className="mx-auto mb-3 text-stone-300" />
              {courses.length === 0 ? (
                <>New online courses launching soon. <Link to="/contact" className="text-[#f38838] font-semibold">Get notified</Link>.</>
              ) : (
                <>No courses match those filters. <button onClick={() => { setCategory(ALL); setQ(""); }} className="text-[#f38838] font-semibold underline">Clear filters</button></>
              )}
            </div>
          )}
        </div>

        <div className="mt-16 card-pp p-10 lg:p-14 bg-stone-100 text-center">
          <Editable id="courses_online.cta.heading" as="h2" className="font-heading text-2xl lg:text-3xl font-bold text-stone-900 mb-3" defaultHtml="Prefer hands-on training?" />
          <Editable id="courses_online.cta.body" as="p" className="text-stone-600 mb-6 max-w-2xl mx-auto" defaultHtml="If you&#39;d rather train with the team in person — at one of our Sunshine Coast halls or in your home — explore our in-person classes." />
          <Link to="/courses/in-person" className="btn-secondary inline-flex items-center gap-2">
            <Editable id="courses_online.cta.button" inline as="span" defaultHtml="See In-Person Classes" /> <ArrowRight size={18} weight="bold" />
          </Link>
        </div>
      </section>

      <PublicFooter />
      <ChatWidget />
    </div>
  );
}
